import { getVistaPayrollApprovedTimesheets, generateVistaBatchExportForEmployees, generateVistaBatchExportTimeOnlyForEmployees, generateVistaBatchExportPieceworksOnlyForEmployees, getJobStatus, generateBatchExportV2ForEmployees, type VistaExportTypeV2 } from "../api/vistaBatchExport";
import { fetchBatchReports } from "../dataLayer/v1BatchExport";
import { EmployeeTimeSheetWithDays, JobStatus, JobStatusEnum } from "../interfaces/V2Interface";

class VistaBatchExportManager {

    // Method to get employees by supervisor (returns full payroll-approved response including days)
    async getEmployees(
        supervisorId: string,
        payPeriodID: number
        ): Promise<EmployeeTimeSheetWithDays[]> {
        try {
            const employees = await getVistaPayrollApprovedTimesheets(parseInt(supervisorId), payPeriodID);
            // Preserve full response (including days); normalize employeeID to string
            return employees.map(emp => ({
                ...emp,
                altEmployeeID: emp.altEmployeeID ?? '',
                employeeID: (emp.employeeID ?? '').toString()
            }));
        } catch (error) {
            console.error("Error getting Vista employees:", error);
            throw error;
        }
    }

    // Method to generate Vista batch export (starts async job)
    async generateVistaBatchExport(
        supervisorID: number,
        payPeriodID: number,
        employeeIDs: string[]
    ): Promise<number> {
        try {
            const jobResponse = await generateVistaBatchExportForEmployees(supervisorID, payPeriodID, employeeIDs);
            return jobResponse.jobID;
        } catch (error) {
            console.error("Error generating Vista batch export:", error);
            throw error;
        }
    }

    // Method to generate Vista batch export - Time Only (starts async job)
    async generateVistaBatchExportTimeOnly(
        supervisorID: number,
        payPeriodID: number,
        employeeIDs: string[]
    ): Promise<number> {
        try {
            const jobResponse = await generateVistaBatchExportTimeOnlyForEmployees(supervisorID, payPeriodID, employeeIDs);
            return jobResponse.jobID;
        } catch (error) {
            console.error("Error generating Vista batch export (time only):", error);
            throw error;
        }
    }

    // Method to generate Vista batch export - Pieceworks Only (starts async job)
    async generateVistaBatchExportPieceworksOnly(
        supervisorID: number,
        payPeriodID: number,
        employeeIDs: string[]
    ): Promise<number> {
        try {
            const jobResponse = await generateVistaBatchExportPieceworksOnlyForEmployees(supervisorID, payPeriodID, employeeIDs);
            return jobResponse.jobID;
        } catch (error) {
            console.error("Error generating Vista batch export (pieceworks only):", error);
            throw error;
        }
    }

    // V2: Single method with exportType ('time' | 'pieceworks' | 'both')
    async generateVistaBatchExportV2(
        supervisorID: number,
        payPeriodID: number,
        employeeIDs: string[],
        exportType: VistaExportTypeV2
    ): Promise<number> {
        try {
            const jobResponse = await generateBatchExportV2ForEmployees(supervisorID, payPeriodID, employeeIDs, exportType);
            return jobResponse.jobID;
        } catch (error) {
            console.error("Error generating Vista batch export V2:", error);
            throw error;
        }
    }

    // Method to poll job status until completion
    async pollJobStatus(
        jobID: number,
        onProgress?: (status: JobStatus) => void
    ): Promise<{ batchId: number; keyID: number; totalEmployees: number; totalEntries: number; totalPieces: number } | { error: any }> {
        return new Promise((resolve, reject) => {
            const pollInterval = setInterval(async () => {
                try {
                    const jobStatus = await getJobStatus(jobID);
                    
                    // Call progress callback if provided
                    if (onProgress) {
                        onProgress(jobStatus);
                    }
                    
                    if (jobStatus.status === JobStatusEnum.COMPLETED) {
                        clearInterval(pollInterval);
                        
                        // Extract batchId and keyID from resultData
                        if (jobStatus.resultData && (jobStatus.resultData.batchId && jobStatus.resultData.keyID) || (jobStatus.resultData.totalEmployees && jobStatus.resultData.totalPieces)) {
                            resolve({
                                batchId: jobStatus.resultData.batchId,
                                keyID: jobStatus.resultData.keyID,
                                totalEmployees: jobStatus.resultData.totalEmployees,
                                totalEntries: jobStatus.resultData.totalEntries,
                                totalPieces: jobStatus.resultData.totalPieces
                            });
                        } else {
                            reject(new Error("Job completed but no batch data found"));
                        }
                    } else if (jobStatus.status === JobStatusEnum.FAILED) {
                        clearInterval(pollInterval);
                        resolve({ error: jobStatus.resultData });
                    }
                    // Continue polling for PENDING and PROCESSING statuses
                } catch (error) {
                    clearInterval(pollInterval);
                    reject(error);
                }
            }, 5000); // Poll every 5 seconds
        });
    }

    // Method to get batch reports (shared with V1)
    async getBatchReports(batchId: string): Promise<any> {
        try {
            const reports = await fetchBatchReports(batchId);
            return reports;
        } catch (error) {
            console.error("Error getting Vista batch reports:", error);
            throw error;
        }
    }
}

// Exporting an instance of VistaBatchExportManager to use throughout the application
export default new VistaBatchExportManager(); 